import * as Sentry from '@sentry/nuxt';

if (useRuntimeConfig().public.sentry.dsn) {
    Sentry.init({
        // If set up, you can use your runtime config here
        dsn: useRuntimeConfig().public.sentry.dsn,
        environment: useRuntimeConfig().public.sentry.environment,

        // Limit automatic collection of personal and request data.
        dataCollection: {
            userInfo: false,
            cookies: false,
            httpHeaders: false,
            httpBodies: [],
        },

        // Trace 5% of all transactions for performance monitoring
        tracesSampleRate: 0.05,

        // No replays in general
        replaysSessionSampleRate: 0,

        // If the entire session is not sampled, use the below sample rate to sample
        // sessions when an error occurs.
        replaysOnErrorSampleRate: 1.0,

        // Make sure attached test data is nested deeper
        normalizeDepth: 8,

        ignoreErrors: [
            'Unexpected end of JSON input',
            'Maximum call stack size exceeded',
            'Unexpected EOF',
            '<no response>',
            'logMutedMessage',
            'Booking not found',
            'Importing a module script failed',
            'Failed to fetch dynamically imported module',
            // Browser extensions and social-media webviews inject these bridges.
            /window\.weixinPostMessageHandlers\.weixinDispatchMessage\.postMessage/i,
            /window\.webkit\.messageHandlers/i,
            /Invalid call to runtime\.sendMessage\(\)\. Tab not found/i,
        ],
        denyUrls: [
            'otBannerSdk.js',
            'otSDKStub.js',
            /chrome-extension/i,
            /webkit-masked-url/i,
            'keycloak.js',
        ],

        /**
         * Filter out certain breadcrumbs
         */
        beforeBreadcrumb(breadcrumb) {
            if (
                (breadcrumb.type === 'http' && breadcrumb.data?.url.includes('cookielaw'))
                || (breadcrumb.type === 'http' && breadcrumb.data?.url.includes('cdn-cgi'))
                || (breadcrumb.type === 'http' && breadcrumb.data?.url.includes('sgtm.jungfrau.ch'))
                || (breadcrumb.type === 'http' && breadcrumb.data?.url.includes('cfapi/client-classification/v01'))
            ) {
                return null;
            }
            return breadcrumb;
        },

        /** Drop errors from known injected scripts without hiding app errors. */
        beforeSend(event) {
            const exception = event.exception?.values?.[0];
            const message = String(exception?.value ?? event.message ?? '');
            const frames = exception?.stacktrace?.frames ?? [];
            const filenames = frames.map(frame => frame.filename ?? '');

            // Privacy software and network policy commonly block the tagging host.
            if (
                /Failed to fetch/i.test(message)
                && filenames.some(filename => filename.includes('sgtm.jungfrau.ch') || filename.includes('/gtag/js'))
            ) {
                return null;
            }

            // Some Android browsers inject malformed inline scripts. Only ignore
            // line 1 in the document; errors in generated Nuxt assets stay visible.
            if (
                /Unexpected token ['"]?else['"]?/i.test(message)
                && frames.length > 0
                && frames.every(frame => frame.lineno === 1 && !/\.m?js(?:\?|$)/i.test(frame.filename ?? ''))
            ) {
                return null;
            }

            return event;
        },

        // Attach Pinia State to Sentry
        integrations: [
            Sentry.replayIntegration({

                maskAllText: false,
                maskAllInputs: true,
                mask: ['[data-sentry-mask]'],
                unmask: ['[data-sentry-unmask]'],

                // Ignore some errors from storing replays
                beforeErrorSampling: (event) => {
                    const ignored = [
                        'Importing a module script failed',
                        'Type Error: Load failed',
                        'Failed to fetch dynamically imported module',
                        'Booking not found',
                    ].map(s => s.toLowerCase());
                    const message = String(event?.exception?.values?.[0]?.value ?? event?.message ?? '').toLowerCase();
                    return !ignored.some(substr => message.includes(substr));
                },
            }),
            Sentry.piniaIntegration(usePinia(), {
                stateTransformer: (state) => {
                    // Deep clone to strip proxies and reactivity
                    let transformedState = '';
                    try {
                        transformedState = JSON.parse(JSON.stringify(state));
                    }
                    catch (error) {

                    }

                    // Recursively remove specific keys anywhere in the object tree
                    function removeKeys(obj: any, keys: string[], overwriteEmpty = false) {
                        if (obj === null || typeof obj !== 'object')
                            return;
                        if (Array.isArray(obj)) {
                            for (const item of obj) removeKeys(item, keys);
                            return;
                        }
                        for (const key of keys) {
                            if (Object.hasOwn(obj, key)) {
                                if (overwriteEmpty) {
                                    obj[key] = '[removed]';
                                }
                                else {
                                    delete obj[key];
                                }
                            }
                        }
                        for (const value of Object.values(obj)) {
                            removeKeys(value, keys);
                        }
                    }

                    // Remove some keys from state data, as too large
                    removeKeys(transformedState, ['legs', 'accommodation', 'config']);
                    // Erase keys for privacy reasons
                    removeKeys(transformedState, ['firstName', 'lastName', 'gender', 'email', 'phoneNumber', 'street', 'postCode', 'city', 'countryCode'], true);
                    return transformedState;
                },
            }),
            Sentry.breadcrumbsIntegration({
                dom: {
                    serializeAttribute: ['data-sentry-id'],
                },
            }),
        ],
    });
}
